// SEO long-tail landing pages — frontend-only.
// Each entry targets a specific search-intent phrase + maps back to a parent
// service discipline (used for the "related discipline" CTA at the bottom).
//
// Routes: /jasa/:slug
// Bilingual ID/EN content is rendered based on the active language.

export const LANDING_PAGES = [
  {
    slug: "pembuatan-website-perusahaan",
    parentService: "web-app-development",
    accent: "#5b4bff",
    eyebrow: { id: "Pembuatan Website Perusahaan", en: "Corporate Website Development" },
    h1: {
      id: "Pembuatan Website Perusahaan & Korporat di Indonesia",
      en: "Corporate & Enterprise Website Development in Indonesia",
    },
    sub: {
      id: "Situs korporat berperforma tinggi dengan CMS yang dapat dipelihara tim Anda — bukan agency.",
      en: "High-performance corporate sites with a CMS your team can maintain — not an agency.",
    },
    seo: {
      title: {
        id: "Pembuatan Website Perusahaan Indonesia — Makario IT",
        en: "Corporate Website Development Indonesia — Makario IT",
      },
      description: {
        id: "Layanan pembuatan website perusahaan berperforma tinggi, SEO-ready, dan dapat diaudit. Stack modern React/Next.js + headless CMS.",
        en: "High-performance, SEO-ready, auditable corporate website development. Modern React/Next.js + headless CMS stack.",
      },
      keywords:
        "pembuatan website perusahaan, jasa website korporat, web development indonesia, website company profile, next.js indonesia",
    },
    intro: {
      id: "Sebagian besar website perusahaan di Indonesia masih dibangun di atas template WordPress yang lambat, sulit di-update, dan tidak dapat dimiliki oleh tim internal. Kami merancang situs korporat sebagai aset bisnis: arsitektur modern, CMS headless, observability sejak hari pertama, dan dokumentasi yang dapat diaudit.",
      en: "Most corporate websites in Indonesia are still built on slow WordPress templates that internal teams can't truly maintain. We design corporate sites as business assets: modern architecture, headless CMS, observability from day one, and auditable documentation.",
    },
    valueProps: [
      {
        title: { id: "Performa setara produk SaaS", en: "Performance on par with SaaS products" },
        desc: {
          id: "Skor Lighthouse 95+ di mobile sebagai standar minimum, bukan ambisi.",
          en: "Lighthouse 95+ on mobile is the minimum bar, not the aspiration.",
        },
      },
      {
        title: { id: "CMS yang dimiliki tim Anda", en: "A CMS your team owns" },
        desc: {
          id: "Sanity, Strapi, atau Payload — pilih sesuai kebutuhan editor, bukan kebutuhan agency.",
          en: "Sanity, Strapi, or Payload — chosen for your editors' needs, not the agency's.",
        },
      },
      {
        title: { id: "SEO teknis sebagai default", en: "Technical SEO as a default" },
        desc: {
          id: "Schema.org, sitemap dinamis, OG dinamis, dan Core Web Vitals dimonitor sejak rilis.",
          en: "Schema.org, dynamic sitemap, dynamic OG, and Core Web Vitals monitored from launch.",
        },
      },
      {
        title: { id: "Multi-bahasa siap pakai", en: "Multilingual-ready" },
        desc: {
          id: "Bahasa Indonesia + Inggris (atau lebih) dengan rute SEO-friendly, bukan plugin tempelan.",
          en: "Indonesian + English (or more) with SEO-friendly routing — no bolt-on plugins.",
        },
      },
    ],
    bestFor: {
      id: ["Korporat lintas industri yang ingin lepas dari template WordPress", "Perusahaan dengan tim internal yang ingin self-publish", "Situs investor relations & rilis berita", "Halaman karir + ATS tipis terintegrasi"],
      en: ["Corporates moving off WordPress templates", "Companies whose internal teams want to self-publish", "Investor relations & press release hubs", "Careers + lightweight ATS-integrated pages"],
    },
    faqs: [
      {
        q: { id: "Berapa lama waktu pengembangannya?", en: "How long does development take?" },
        a: {
          id: "Untuk website korporat tipikal: 6–10 minggu sejak kick-off hingga go-live, tergantung kompleksitas konten dan jumlah modul.",
          en: "For a typical corporate site: 6–10 weeks from kick-off to go-live, depending on content complexity and module count.",
        },
      },
      {
        q: { id: "Apakah tim kami bisa update sendiri setelah live?", en: "Can our team self-update after go-live?" },
        a: {
          id: "Ya — kami serahkan brand book, CMS terlatih, dan dokumentasi operasi. Tidak ada lock-in agency.",
          en: "Yes — we hand over the brand book, a trained CMS, and ops documentation. No agency lock-in.",
        },
      },
      {
        q: { id: "Bagaimana dengan hosting & maintenance?", en: "What about hosting & maintenance?" },
        a: {
          id: "Kami merekomendasikan Vercel/Netlify + Cloudflare untuk delivery global. Maintenance retainer opsional, tidak wajib.",
          en: "We recommend Vercel/Netlify + Cloudflare for global delivery. Optional maintenance retainer — never forced.",
        },
      },
      {
        q: { id: "Apakah ada garansi performa?", en: "Is there a performance guarantee?" },
        a: {
          id: "Ya. Target Lighthouse 95+ mobile dan TTFB < 200ms di Jakarta tertulis dalam kontrak.",
          en: "Yes. Lighthouse 95+ on mobile and TTFB < 200ms from Jakarta are written into the contract.",
        },
      },
    ],
  },
  {
    slug: "jasa-cyber-security-iso-27001",
    parentService: "cyber-security",
    accent: "#8174ff",
    eyebrow: { id: "Cyber Security & ISO 27001", en: "Cyber Security & ISO 27001" },
    h1: {
      id: "Jasa Cyber Security & Kesiapan ISO 27001 untuk Indonesia",
      en: "Cyber Security Services & ISO 27001 Readiness for Indonesia",
    },
    sub: {
      id: "Pertahanan berlapis untuk aset, kepatuhan UU PDP, dan ketenangan tim Anda — sebelum, bukan sesudah, audit.",
      en: "Layered defence for assets, PDP compliance, and team confidence — before, not after, the audit.",
    },
    seo: {
      title: {
        id: "Jasa Cyber Security & Kesiapan ISO 27001 Indonesia — Makario",
        en: "Cyber Security & ISO 27001 Readiness Indonesia — Makario",
      },
      description: {
        id: "Audit keamanan, penetration testing, dan kesiapan ISO 27001 + UU PDP Indonesia. Pendekatan ‘security as a habit’, bukan one-time audit.",
        en: "Security audits, penetration testing, and ISO 27001 + Indonesian PDP readiness. Security as a habit, not a one-time audit.",
      },
      keywords:
        "jasa cyber security indonesia, iso 27001 indonesia, uu pdp, penetration testing, audit keamanan, konsultan keamanan siber",
    },
    intro: {
      id: "Lulus audit ISO 27001 tetapi tetap mengalami insiden adalah pola yang sangat lazim di Indonesia. Penyebabnya bukan kebijakannya — melainkan jaraknya dari kerja harian. Kami menutup jarak itu dengan policy-as-code, drill rutin, dan threat modeling yang menempel pada siklus pengembangan.",
      en: "Passing an ISO 27001 audit while incidents still happen is a very common pattern in Indonesia. The cause isn't the policy — it's its distance from daily work. We close that gap with policy-as-code, regular drills, and threat modelling baked into the development cycle.",
    },
    valueProps: [
      {
        title: { id: "Policy-as-code di pipeline", en: "Policy-as-code in the pipeline" },
        desc: {
          id: "Aturan keamanan ditegakkan di CI/CD, bukan di PDF yang tidak pernah dibaca.",
          en: "Security rules enforced in CI/CD, not in PDFs nobody reads.",
        },
      },
      {
        title: { id: "Penetration testing yang terdokumentasi", en: "Documented penetration testing" },
        desc: {
          id: "Pengujian web, mobile, dan cloud dengan laporan yang dapat diaudit untuk asuransi & regulator.",
          en: "Web, mobile, and cloud testing with audit-ready reports for insurers & regulators.",
        },
      },
      {
        title: { id: "Kesiapan UU PDP Indonesia", en: "Indonesian PDP Law readiness" },
        desc: {
          id: "Data classification, DPIA, dan record-of-processing sesuai UU 27/2022 PDP.",
          en: "Data classification, DPIA, and record-of-processing aligned with PDP Law 27/2022.",
        },
      },
      {
        title: { id: "Tabletop drill kuartalan", en: "Quarterly tabletop drills" },
        desc: {
          id: "Tim Anda berlatih merespons insiden, bukan hanya membaca runbook.",
          en: "Your team practises incident response — not just reads the runbook.",
        },
      },
    ],
    bestFor: {
      id: ["Perusahaan yang menyiapkan sertifikasi ISO 27001 pertama", "Fintech & e-commerce yang menyimpan data sensitif pelanggan", "Organisasi yang habis insiden dan butuh hardening pasca-incident", "Lembaga publik dengan kepatuhan UU PDP"],
      en: ["Companies preparing first-time ISO 27001 certification", "Fintech & e-commerce handling sensitive customer data", "Organisations hardening after an incident", "Public institutions complying with PDP Law"],
    },
    faqs: [
      {
        q: { id: "Berapa lama proses kesiapan ISO 27001?", en: "How long does ISO 27001 readiness take?" },
        a: {
          id: "Biasanya 4–6 bulan dari diagnostik hingga readiness review, tergantung kondisi awal kontrol keamanan.",
          en: "Typically 4–6 months from diagnostic to readiness review, depending on existing security controls.",
        },
      },
      {
        q: { id: "Apakah kami butuh tim security in-house?", en: "Do we need an in-house security team?" },
        a: {
          id: "Tidak — kami bekerja dengan tim IT eksisting dan membantu membentuk fungsi security sebagai bagian dari engineering, bukan silo.",
          en: "No — we work with your existing IT team and help shape security as part of engineering, not a silo.",
        },
      },
      {
        q: { id: "Apakah hasilnya dapat dibawa ke auditor eksternal?", en: "Are the deliverables auditor-ready?" },
        a: {
          id: "Ya. Laporan kami disusun mengikuti format Statement of Applicability dan dapat dibawa langsung ke auditor.",
          en: "Yes. Our reports follow Statement of Applicability formatting and go straight to the auditor.",
        },
      },
      {
        q: { id: "Bagaimana penanganan kerahasiaan saat pentest?", en: "How do you handle confidentiality during pentest?" },
        a: {
          id: "NDA dua arah, scoping yang jelas, dan handling laporan via secure channel terenkripsi end-to-end.",
          en: "Mutual NDA, clear scoping, and report handling via end-to-end encrypted secure channels.",
        },
      },
    ],
  },
  {
    slug: "pengembangan-aplikasi-mobile-indonesia",
    parentService: "web-app-development",
    accent: "#5b4bff",
    eyebrow: { id: "Pengembangan Aplikasi Mobile", en: "Mobile App Development" },
    h1: {
      id: "Pengembangan Aplikasi Mobile (iOS & Android) untuk Konteks Indonesia",
      en: "Mobile App Development (iOS & Android) Built for Indonesian Reality",
    },
    sub: {
      id: "React Native & Flutter yang stabil di ponsel di bawah Rp 4 juta, di jaringan 3G, dan di tangan pengguna nyata.",
      en: "React Native & Flutter that stays stable on phones under IDR 4M, on 3G networks, and in real users' hands.",
    },
    seo: {
      title: {
        id: "Jasa Pengembangan Aplikasi Mobile iOS & Android — Makario Indonesia",
        en: "Mobile App Development iOS & Android — Makario Indonesia",
      },
      description: {
        id: "Pengembangan aplikasi mobile React Native & Flutter yang dirancang untuk realita perangkat dan jaringan di Indonesia. Offline-first, hemat baterai, terukur.",
        en: "React Native & Flutter mobile apps designed for Indonesian device and network realities. Offline-first, battery-light, measurable.",
      },
      keywords:
        "pengembangan aplikasi mobile, jasa aplikasi android, jasa aplikasi ios, react native indonesia, flutter indonesia, aplikasi mobile umkm",
    },
    intro: {
      id: "Aplikasi mobile yang sukses di benchmark global sering gagal di Indonesia karena diuji hanya di iPhone Pro dan WiFi kantor. 87% pengguna kami selama tiga tahun terakhir mengakses produk klien lewat ponsel di bawah Rp 4 juta. Kami merancang dan menguji untuk kenyataan itu.",
      en: "Mobile apps that succeed on global benchmarks often fail in Indonesia because they're tested only on iPhone Pros and office WiFi. 87% of users we've seen over three years access clients' products via phones priced under IDR 4M. We design and test for that reality.",
    },
    valueProps: [
      {
        title: { id: "Offline-first sebagai default", en: "Offline-first by default" },
        desc: {
          id: "Sinkronisasi cerdas, conflict resolution, dan UX yang menjelaskan status koneksi.",
          en: "Smart sync, conflict resolution, and UX that communicates connection state.",
        },
      },
      {
        title: { id: "Uji di perangkat nyata Indonesia", en: "Tested on real Indonesian devices" },
        desc: {
          id: "Device lab internal mencakup Xiaomi, Oppo, Vivo, Realme kelas entry — bukan hanya flagship.",
          en: "Internal device lab covers entry-tier Xiaomi, Oppo, Vivo, Realme — not just flagships.",
        },
      },
      {
        title: { id: "Ukuran APK / IPA kecil", en: "Small APK / IPA bundles" },
        desc: {
          id: "Modular code-splitting, asset pruning, dan dynamic delivery agar pengguna mau install.",
          en: "Modular code-splitting, asset pruning, and dynamic delivery so users actually install.",
        },
      },
      {
        title: { id: "Analytics & crash reporting sejak v0.1", en: "Analytics & crash reporting from v0.1" },
        desc: {
          id: "Sentry + Amplitude/Mixpanel ter-wire sejak hari pertama. Tidak ada peluncuran buta.",
          en: "Sentry + Amplitude/Mixpanel wired from day one. No blind launches.",
        },
      },
    ],
    bestFor: {
      id: ["Startup yang akan launch ke pasar Indonesia pertama kali", "Brand F&B / retail yang butuh loyalty app", "Aplikasi internal lapangan (sales, kurir, surveyor)", "Aplikasi yang harus berjalan di area dengan koneksi tidak stabil"],
      en: ["Startups launching into Indonesia first", "F&B / retail brands needing loyalty apps", "Internal field apps (sales, couriers, surveyors)", "Apps that must run in low-connectivity regions"],
    },
    faqs: [
      {
        q: { id: "React Native atau Flutter — mana yang lebih baik?", en: "React Native or Flutter — which is better?" },
        a: {
          id: "Tergantung tim, integrasi native, dan target performa. Kami audit konteks Anda dulu sebelum merekomendasikan.",
          en: "Depends on the team, native integrations, and performance targets. We audit your context before recommending.",
        },
      },
      {
        q: { id: "Berapa biaya pengembangan aplikasi mobile?", en: "How much does a mobile app cost?" },
        a: {
          id: "MVP umumnya Rp 250–600jt tergantung kompleksitas. Kami selalu beri breakdown per modul, bukan harga gelondongan.",
          en: "MVPs typically range from IDR 250–600M depending on complexity. We always provide a per-module breakdown — never a black-box quote.",
        },
      },
      {
        q: { id: "Apakah ada handover untuk publish ke Play Store / App Store?", en: "Do you handle Play Store / App Store publishing?" },
        a: {
          id: "Ya. Kami pegang proses review, screenshot, copy listing, dan rilis bertahap (staged rollout) sampai 100% pengguna.",
          en: "Yes. We handle review submission, screenshots, listing copy, and staged rollout to 100% of users.",
        },
      },
      {
        q: { id: "Bagaimana dengan maintenance pasca-launch?", en: "What about post-launch maintenance?" },
        a: {
          id: "Retainer bulanan opsional mencakup bug fix, OS update support, dan eksperimen produk.",
          en: "Optional monthly retainer covers bug fixes, OS update support, and product experimentation.",
        },
      },
    ],
  },
  {
    slug: "digital-marketing-umkm-indonesia",
    parentService: "digital-marketing",
    accent: "#8174ff",
    eyebrow: { id: "Digital Marketing untuk UMKM", en: "Digital Marketing for SMEs" },
    h1: {
      id: "Digital Marketing untuk UMKM Indonesia — Pertumbuhan Terukur",
      en: "Digital Marketing for Indonesian SMEs — Measurable Growth",
    },
    sub: {
      id: "Eksperimen pertumbuhan yang dapat dijalankan tanpa anggaran enterprise. Kemenangan kecil yang berlipat ganda.",
      en: "Growth experiments any SME can run without an enterprise budget. Small wins that compound.",
    },
    seo: {
      title: {
        id: "Jasa Digital Marketing UMKM Indonesia — Makario IT",
        en: "Digital Marketing Services for Indonesian SMEs — Makario IT",
      },
      description: {
        id: "Jasa digital marketing fokus pada UMKM Indonesia: paid media Meta/Google/TikTok, content engine, dan analytics yang dimiliki tim Anda.",
        en: "Digital marketing services focused on Indonesian SMEs: Meta/Google/TikTok paid media, content engine, and analytics owned by your team.",
      },
      keywords:
        "digital marketing umkm, jasa digital marketing indonesia, iklan facebook umkm, tiktok ads, jasa seo, konten marketing",
    },
    intro: {
      id: "Banyak UMKM Indonesia merasa digital marketing adalah proyek besar yang hanya terjangkau oleh tier-1. Pengalaman kami menunjukkan sebaliknya: kemenangan kecil yang konsisten jauh lebih kuat daripada satu inisiatif besar yang setengah jalan.",
      en: "Many Indonesian SMEs feel digital marketing is a large project only top-tier corporations can afford. Our experience says otherwise: small consistent wins are far more powerful than one grand initiative that stalls.",
    },
    valueProps: [
      {
        title: { id: "Mulai dari satu kanal yang paling jujur", en: "Start with the single most honest channel" },
        desc: {
          id: "Kami tidak menyarankan 5 kanal sekaligus. Pilih satu, kuasai, lalu tambahkan.",
          en: "We don't recommend 5 channels at once. Pick one, master it, then add.",
        },
      },
      {
        title: { id: "Creative yang berbicara Indonesia", en: "Creative that speaks Indonesia" },
        desc: {
          id: "Tone lokal, referensi lokal, dan format yang berfungsi di FYP Indonesia — bukan terjemahan iklan global.",
          en: "Local tone, local references, and formats that work on Indonesian FYP — not translated global ads.",
        },
      },
      {
        title: { id: "Analytics yang dapat dibaca tim Anda", en: "Analytics your team can actually read" },
        desc: {
          id: "Dashboard ringkas yang fokus pada metrik bisnis, bukan vanity metrics.",
          en: "Lean dashboards focused on business metrics, not vanity numbers.",
        },
      },
      {
        title: { id: "Kontrak fleksibel, tanpa lock-in tahunan", en: "Flexible engagement, no annual lock-in" },
        desc: {
          id: "Mulai dari 3 bulan. Lanjutkan jika hasil terukur, bukan karena kontrak memaksa.",
          en: "Start from 3 months. Continue only if results are measurable — never because a contract forces it.",
        },
      },
    ],
    bestFor: {
      id: ["UMKM dengan budget marketing < Rp 50jt/bulan", "Brand F&B atau retail yang baru memulai iklan berbayar", "DTC brand yang ingin keluar dari ketergantungan marketplace", "Layanan B2B yang ingin membangun pipeline outbound"],
      en: ["SMEs with marketing budgets under IDR 50M/month", "F&B or retail brands starting paid ads for the first time", "DTC brands wanting to escape marketplace dependency", "B2B services building an outbound pipeline"],
    },
    faqs: [
      {
        q: { id: "Berapa minimum budget untuk mulai?", en: "What's the minimum budget to start?" },
        a: {
          id: "Kami merekomendasikan minimum Rp 20jt/bulan untuk creative + media combined agar eksperimen menghasilkan data berarti.",
          en: "We recommend a minimum of IDR 20M/month for creative + media combined so experiments produce meaningful data.",
        },
      },
      {
        q: { id: "Apakah kami bisa memilih kanal sendiri?", en: "Can we choose channels ourselves?" },
        a: {
          id: "Bisa, tapi kami akan menantang jika datanya menunjukkan sebaliknya. Anda yang memutuskan, kami yang menyediakan argumen.",
          en: "Yes, but we'll push back if the data says otherwise. You decide, we provide the argument.",
        },
      },
      {
        q: { id: "Bagaimana mengukur ROI?", en: "How is ROI measured?" },
        a: {
          id: "Tracking server-side + UTM disiplin + dashboard mingguan. Bukan ‘Facebook bilang ROAS 5×’.",
          en: "Server-side tracking + disciplined UTM + weekly dashboard. Not ‘Facebook says ROAS 5×’.",
        },
      },
      {
        q: { id: "Apakah ada handover ke tim internal nantinya?", en: "Is there a handover to internal team?" },
        a: {
          id: "Ya — pelatihan + dokumentasi proses disertakan agar tim Anda dapat melanjutkan secara mandiri.",
          en: "Yes — training + process docs included so your team can continue independently.",
        },
      },
    ],
  },
  {
    slug: "ui-ux-research-fintech-banking",
    parentService: "ui-ux-research",
    accent: "#5b4bff",
    eyebrow: { id: "UI/UX Research Fintech & Banking", en: "UI/UX Research for Fintech & Banking" },
    h1: {
      id: "UI/UX Research untuk Fintech & Banking Indonesia",
      en: "UI/UX Research for Indonesian Fintech & Banking",
    },
    sub: {
      id: "Riset pengguna yang menerjemahkan perilaku finansial di Indonesia menjadi keputusan produk yang dapat dipertahankan.",
      en: "User research that translates Indonesian financial behaviour into defensible product decisions.",
    },
    seo: {
      title: {
        id: "UI/UX Research Fintech & Banking Indonesia — Makario",
        en: "UI/UX Research for Fintech & Banking Indonesia — Makario",
      },
      description: {
        id: "Riset UX khusus fintech, perbankan, dan layanan keuangan di Indonesia. Mixed-method, etnografi pendek, dan rekomendasi yang dapat langsung dieksekusi.",
        en: "UX research specifically for fintech, banking, and financial services in Indonesia. Mixed-method, short ethnography, and immediately actionable recommendations.",
      },
      keywords:
        "ui ux research fintech, ux research banking indonesia, user research finansial, jasa ux research, ux research mobile banking",
    },
    intro: {
      id: "Wawancara di Jakarta sangat berbeda dari di Surabaya. Daya beli, ekspektasi UX, hingga literasi finansial — semua berbeda. Kami merancang program riset yang mengakomodasi variasi ini dan menghasilkan keputusan produk, bukan deck panjang.",
      en: "Interviews in Jakarta differ sharply from those in Surabaya. Purchasing power, UX expectations, even financial literacy — all vary. We design research programmes that account for this variation and produce product decisions, not lengthy decks.",
    },
    valueProps: [
      {
        title: { id: "Persona lokal, bukan import", en: "Local personas, not imports" },
        desc: {
          id: "Persona dibangun dari data lapangan Indonesia, bukan diterjemahkan dari studi global.",
          en: "Personas built from Indonesian field data — not translated from global studies.",
        },
      },
      {
        title: { id: "Riset etnografi singkat", en: "Short ethnographic studies" },
        desc: {
          id: "4–6 jam observasi di lingkungan asli sering lebih bernilai dari 40 wawancara terjadwal.",
          en: "4–6 hours of observation in real environments is often worth more than 40 scheduled interviews.",
        },
      },
      {
        title: { id: "Recruitment di kota Tier-2 & Tier-3", en: "Recruitment in Tier-2 & Tier-3 cities" },
        desc: {
          id: "Pool partisipan kami mencakup Yogyakarta, Medan, Banjarmasin, Makassar — bukan hanya Jabodetabek.",
          en: "Our participant pool covers Yogyakarta, Medan, Banjarmasin, Makassar — not just Greater Jakarta.",
        },
      },
      {
        title: { id: "Laporan riset-ke-keputusan", en: "Research-to-decision reports" },
        desc: {
          id: "Setiap insight dilampiri rekomendasi prioritas + estimasi dampak, siap masuk backlog.",
          en: "Every insight ships with a prioritised recommendation + impact estimate, ready for the backlog.",
        },
      },
    ],
    bestFor: {
      id: ["Aplikasi mobile banking yang akan rilis ulang", "Fintech baru yang akan launch ke pasar Indonesia", "Produk pinjaman, payment, atau wealth-tech", "Tim produk yang butuh validasi sebelum scale-up"],
      en: ["Mobile banking apps preparing a redesign", "New fintech entering the Indonesian market", "Lending, payments, or wealth-tech products", "Product teams needing validation before scale-up"],
    },
    faqs: [
      {
        q: { id: "Berapa lama tipikalnya satu siklus riset?", en: "How long does one research cycle typically take?" },
        a: {
          id: "Sprint riset standar 4–6 minggu mulai dari framing hingga rekomendasi siap eksekusi.",
          en: "A standard research sprint runs 4–6 weeks from framing to ready-to-execute recommendations.",
        },
      },
      {
        q: { id: "Berapa partisipan biasanya direkrut?", en: "How many participants do you typically recruit?" },
        a: {
          id: "8–12 untuk wawancara kualitatif, 120–300 untuk survei kuantitatif tergantung tingkat kepercayaan yang dibutuhkan.",
          en: "8–12 for qualitative interviews, 120–300 for quantitative surveys depending on the confidence level needed.",
        },
      },
      {
        q: { id: "Apakah kami bisa ikut sesi wawancara?", en: "Can we attend interview sessions?" },
        a: {
          id: "Sangat dianjurkan. Hadir 2–3 sesi membantu tim Anda merasakan riset secara langsung.",
          en: "Highly encouraged. Attending 2–3 sessions helps your team feel the research first-hand.",
        },
      },
      {
        q: { id: "Bagaimana penanganan data sensitif keuangan?", en: "How is sensitive financial data handled?" },
        a: {
          id: "NDA dua arah, anonimisasi data, dan penyimpanan terenkripsi sesuai UU PDP Indonesia.",
          en: "Mutual NDA, data anonymisation, and encrypted storage aligned with Indonesia's PDP Law.",
        },
      },
    ],
  },
];
